import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { propertyAPI, roomAPI, reservationAPI, paymentAPI, inventoryAPI, roomChargeAPI } from '@/services/api';
import PaymentModal from '@/components/PaymentModal';
import { 
  Search, 
  DoorOpen, 
  Phone, 
  User, 
  Calendar, 
  CreditCard, 
  Gift, 
  LogIn, 
  LogOut,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  X,
  Clock,
  Sparkles,
  Wrench,
  DollarSign,
  ArrowRightLeft,
  Shield,
  ShoppingCart,
  Plus,
  Trash2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  disponible: { 
    label: 'Disponible', 
    color: 'bg-emerald-100 border-emerald-400 text-emerald-700',
    badgeColor: 'bg-emerald-500',
    doorAccent: 'bg-emerald-400',
    doorGlow: 'shadow-emerald-400/30',
    ledColor: 'bg-emerald-400',
    icon: Check 
  },
  ocupada: { 
    label: 'Ocupada', 
    color: 'bg-orange-100 border-orange-400 text-orange-700',
    badgeColor: 'bg-orange-500',
    doorAccent: 'bg-orange-400',
    doorGlow: 'shadow-orange-400/30',
    ledColor: 'bg-orange-400',
    icon: User 
  },
  reservada: { 
    label: 'Reservada', 
    color: 'bg-blue-100 border-blue-400 text-blue-700',
    badgeColor: 'bg-blue-500',
    doorAccent: 'bg-blue-400',
    doorGlow: 'shadow-blue-400/30',
    ledColor: 'bg-blue-400',
    icon: Calendar 
  },
  mantenimiento: { 
    label: 'Mantenimiento', 
    color: 'bg-yellow-100 border-yellow-400 text-yellow-700',
    badgeColor: 'bg-yellow-500',
    doorAccent: 'bg-yellow-400',
    doorGlow: 'shadow-yellow-400/30',
    ledColor: 'bg-yellow-400',
    icon: Wrench 
  },
  limpieza: { 
    label: 'Limpieza', 
    color: 'bg-cyan-100 border-cyan-400 text-cyan-700',
    badgeColor: 'bg-cyan-500',
    doorAccent: 'bg-cyan-400',
    doorGlow: 'shadow-cyan-400/30',
    ledColor: 'bg-cyan-400',
    icon: Sparkles 
  }
};

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function HabitacionesPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Edit room state
  const [editRoomOpen, setEditRoomOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomForm, setEditRoomForm] = useState({ nombre: '', tipo: '', capacidad: '', descripcion: '', num_camas: '', tipo_camas: '', num_banos: '', num_habitaciones_internas: '', distribucion: '', amenidades: [] });
  const [deleteRoomConfirm, setDeleteRoomConfirm] = useState(null);

  // Checkout validation state
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  
  // Transfer state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  
  // Credit state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);

  // Consumos state
  const [roomCharges, setRoomCharges] = useState([]);
  const [products, setProducts] = useState([]);
  const [consumoDialogOpen, setConsumoDialogOpen] = useState(false);
  const [consumoForm, setConsumoForm] = useState({ product_id: '', cantidad: '1' });
  const [consumoSaving, setConsumoSaving] = useState(false);
  const [creditClient, setCreditClient] = useState('');
  const [creditPlazo, setCreditPlazo] = useState('30');

  // Guest registration state
  const openEditRoom = (room) => {
    setEditingRoom(room);
    setEditRoomForm({
      nombre: room.nombre || '', tipo: room.tipo || '', capacidad: (room.capacidad || '').toString(),
      descripcion: room.descripcion || '', num_camas: (room.num_camas || '').toString(),
      tipo_camas: room.tipo_camas || '', num_banos: (room.num_banos || '').toString(),
      num_habitaciones_internas: (room.num_habitaciones_internas || '').toString(),
      distribucion: room.distribucion || '', amenidades: room.amenidades || []
    });
    setEditRoomOpen(true);
  };

  const saveEditRoom = async () => {
    try {
      const token = localStorage.getItem('token');
      const update = {};
      if (editRoomForm.nombre) update.nombre = editRoomForm.nombre;
      if (editRoomForm.tipo) update.tipo = editRoomForm.tipo;
      if (editRoomForm.capacidad) update.capacidad = parseInt(editRoomForm.capacidad);
      if (editRoomForm.descripcion !== undefined) update.descripcion = editRoomForm.descripcion;
      if (editRoomForm.num_camas) update.num_camas = parseInt(editRoomForm.num_camas);
      if (editRoomForm.tipo_camas) update.tipo_camas = editRoomForm.tipo_camas;
      if (editRoomForm.num_banos) update.num_banos = parseInt(editRoomForm.num_banos);
      if (editRoomForm.num_habitaciones_internas) update.num_habitaciones_internas = parseInt(editRoomForm.num_habitaciones_internas);
      if (editRoomForm.distribucion) update.distribucion = editRoomForm.distribucion;
      update.amenidades = editRoomForm.amenidades || [];
      
      await axios.put(`${BACKEND_URL}/api/rooms/${editingRoom.id}`, update, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Habitación actualizada');
      setEditRoomOpen(false);
      fetchRoomsAndReservations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
  };

  const deleteRoom = async () => {
    if (!deleteRoomConfirm) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/rooms/${deleteRoomConfirm.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Habitación eliminada');
      setDeleteRoomConfirm(null);
      fetchRoomsAndReservations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const [guestFormOpen, setGuestFormOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({
    guest_tipo_documento: 'cedula',
    guest_nacionalidad: 'nacional',
    guest_ciudad_origen: '',
    guest_documento: '',
    guest_name: '',
    guest_lastname: '',
    guest_phone: '',
    guest_email: '',
  });
  const [savingGuest, setSavingGuest] = useState(false);
  const [deleteReservationOpen, setDeleteReservationOpen] = useState(false);
  const [deletingReservation, setDeletingReservation] = useState(false);

  const openGuestForm = () => {
    if (selectedReservation) {
      setGuestForm({
        guest_tipo_documento: selectedReservation.guest_tipo_documento || 'cedula',
        guest_nacionalidad: selectedReservation.guest_nacionalidad || 'nacional',
        guest_ciudad_origen: selectedReservation.guest_ciudad_origen || '',
        guest_documento: selectedReservation.guest_documento || '',
        guest_name: selectedReservation.guest_name || '',
        guest_lastname: selectedReservation.guest_lastname || '',
        guest_phone: selectedReservation.guest_phone || '',
        guest_email: selectedReservation.guest_email || '',
      });
    }
    setGuestFormOpen(true);
  };

  const handleSaveGuest = async () => {
    if (!selectedReservation) return;
    setSavingGuest(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/reservations/${selectedReservation.id}/guest-registration`,
        guestForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tarjeta de registro guardada');
      setGuestFormOpen(false);
      fetchRoomsAndReservations();
    } catch (e) {
      toast.error('Error al guardar datos del huésped');
    } finally { setSavingGuest(false); }
  };

  const handleDeleteReservation = async () => {
    if (!selectedReservation) return;
    setDeletingReservation(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/reservations/${selectedReservation.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reserva eliminada. Habitación liberada.');
      setDeleteReservationOpen(false);
      setModalOpen(false);
      fetchRoomsAndReservations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar reserva');
    } finally { setDeletingReservation(false); }
  };
  const [reservationPaymentInfo, setReservationPaymentInfo] = useState({});

  const today = new Date();

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchRoomsAndReservations();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      // Check if admin - if so, get all properties
      const token = localStorage.getItem('token');
      let response;
      
      try {
        // Try admin endpoint first
        const adminRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/properties/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (adminRes.ok) {
          const data = await adminRes.json();
          setProperties(data);
          setLoading(false);
          return;
        }
      } catch (e) {
        // Not admin, continue with regular endpoint
      }
      
      response = await propertyAPI.getAll();
      setProperties(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsAndReservations = async () => {
    try {
      const [roomsRes, reservationsRes] = await Promise.all([
        roomAPI.getAll(selectedProperty),
        reservationAPI.getAll(selectedProperty)
      ]);
      setRooms(roomsRes.data);
      setReservations(reservationsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getRoomStatus = (room) => {
    // Get all reservations for this room covering today
    const todayReservations = reservations.filter(res => {
      const checkIn = parseISO(res.fecha_checkin);
      const checkOut = parseISO(res.fecha_checkout);
      return res.room_id === room.id && 
             today >= checkIn && today < checkOut;
    });

    // Prioritize actionable reservations: checked_in > confirmada/pendiente > checked_out
    const priorityOrder = ['checked_in', 'confirmada', 'pendiente', 'reservada', 'checked_out', 'cancelada'];
    const sortedReservations = [...todayReservations].sort((a, b) => {
      return priorityOrder.indexOf(a.estado) - priorityOrder.indexOf(b.estado);
    });
    
    const todayReservation = sortedReservations[0];

    if (todayReservation) {
      if (todayReservation.estado === 'checked_in' || todayReservation.estado === 'ocupada') {
        return { status: 'ocupada', reservation: todayReservation };
      }
      if (todayReservation.estado === 'checked_out') {
        // Room needs attention after checkout, but show the reservation for context
        return { status: room.estado || 'mantenimiento', reservation: todayReservation };
      }
      if (isSameDay(parseISO(todayReservation.fecha_checkin), today)) {
        return { status: 'reservada', reservation: todayReservation };
      }
      return { status: 'ocupada', reservation: todayReservation };
    }

    // Check room status
    if (room.estado === 'mantenimiento') return { status: 'mantenimiento', reservation: null };
    if (room.estado === 'limpieza') return { status: 'limpieza', reservation: null };
    
    return { status: 'disponible', reservation: null };
  };

  const getStatusCounts = () => {
    const counts = { disponible: 0, ocupada: 0, reservada: 0, mantenimiento: 0, limpieza: 0 };
    rooms.forEach(room => {
      const { status } = getRoomStatus(room);
      counts[status]++;
    });
    return counts;
  };

  const filteredRooms = rooms.filter(room => {
    const { status } = getRoomStatus(room);
    const matchesFilter = filter === 'todos' || status === filter;
    const matchesSearch = room.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          room.tipo?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const fetchPaymentInfoForReservation = async (reservationId) => {
    try {
      const response = await paymentAPI.getByReservation(reservationId);
      setReservationPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
      setReservationPaymentInfo({});
    }
  };

  const handleRoomClick = (room) => {
    const { status, reservation } = getRoomStatus(room);
    setSelectedRoom({ ...room, currentStatus: status });
    setSelectedReservation(reservation);
    setModalOpen(true);
    if (reservation) {
      fetchPaymentInfoForReservation(reservation.id);
      loadRoomCharges(reservation.id);
    } else {
      setReservationPaymentInfo({});
      setRoomCharges([]);
    }
  };

  const loadRoomCharges = async (reservationId) => {
    try {
      const res = await roomChargeAPI.getByReservation(reservationId);
      setRoomCharges(res.data);
    } catch { setRoomCharges([]); }
  };

  const loadProducts = async () => {
    if (!selectedProperty) return;
    const propId = typeof selectedProperty === 'string' ? selectedProperty : selectedProperty.id;
    try {
      const res = await inventoryAPI.getProducts(propId);
      setProducts(res.data || []);
    } catch { setProducts([]); }
  };

  const openConsumoDialog = () => {
    setConsumoForm({ product_id: '', cantidad: '1' });
    loadProducts();
    setConsumoDialogOpen(true);
  };

  const handleAddConsumo = async () => {
    if (!consumoForm.product_id || !selectedReservation) return;
    const product = products.find(p => p.id === consumoForm.product_id);
    if (!product) return;
    const qty = parseInt(consumoForm.cantidad || 1);
    if (qty < 1) { toast.error('Cantidad inválida'); return; }
    setConsumoSaving(true);
    try {
      await roomChargeAPI.create({
        reservation_id: selectedReservation.id,
        product_id: product.id,
        cantidad: qty,
        precio_unitario: product.precio_venta,
        concepto: `Consumo ${product.nombre}`,
        notas: null
      });
      toast.success(`${product.nombre} x${qty} cargado`);
      setConsumoDialogOpen(false);
      loadRoomCharges(selectedReservation.id);
      fetchPaymentInfoForReservation(selectedReservation.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cargar consumo'); }
    finally { setConsumoSaving(false); }
  };

  const handleDeleteCharge = async (chargeId) => {
    try {
      await roomChargeAPI.delete(chargeId);
      toast.success('Cargo eliminado');
      loadRoomCharges(selectedReservation.id);
      fetchPaymentInfoForReservation(selectedReservation.id);
    } catch { toast.error('Error al eliminar cargo'); }
  };

  const handleCheckIn = async () => {
    if (!selectedReservation) return;
    try {
      await reservationAPI.checkin(selectedReservation.id);
      toast.success('Check-in realizado exitosamente');
      setModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar check-in');
    }
  };

  const handleCheckOut = async () => {
    if (!selectedReservation) return;
    // First validate balance
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/reservations/${selectedReservation.id}/checkout-check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      if (data.puede_checkout || data.enviado_a_credito) {
        // Balance is $0 or already sent to credit - proceed
        await reservationAPI.checkout(selectedReservation.id);
        toast.success('Check-out realizado. Habitación en mantenimiento.');
        setModalOpen(false);
        fetchRoomsAndReservations();
      } else {
        // Balance pending - show options dialog
        setCheckoutData(data);
        setCheckoutDialogOpen(true);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al verificar checkout');
    }
  };

  const handleForceCheckout = async () => {
    try {
      await reservationAPI.checkout(selectedReservation.id);
      toast.success('Check-out realizado.');
      setCheckoutDialogOpen(false);
      setModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar check-out');
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget || !selectedReservation) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/reservations/${selectedReservation.id}/transfer`,
        { target_reservation_id: transferTarget },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Cuenta trasladada exitosamente');
      setTransferDialogOpen(false);
      setCheckoutDialogOpen(false);
      setTransferTarget('');
      fetchRoomsAndReservations();
      if (selectedReservation) fetchPaymentInfoForReservation(selectedReservation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al trasladar cuenta');
    }
  };

  const handleSendToCredit = async () => {
    if (!creditClient || !selectedReservation || !checkoutData) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/reservations/${selectedReservation.id}/send-to-credit`,
        {
          client_id: creditClient,
          plazo_dias: parseInt(creditPlazo),
          monto_credito: checkoutData.saldo_pendiente
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saldo enviado a crédito (${creditPlazo} días)`);
      setCreditDialogOpen(false);
      setCheckoutDialogOpen(false);
      setCreditClient('');
      setCreditPlazo('30');
      // Now proceed with checkout
      await reservationAPI.checkout(selectedReservation.id);
      toast.success('Check-out realizado.');
      setModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar a crédito');
    }
  };

  const openTransferDialog = () => {
    // Load other reservations for transfer from checkout data
    setTransferDialogOpen(true);
  };

  const handleSetAvailable = async () => {
    if (!selectedRoom) return;
    try {
      await roomAPI.setAvailable(selectedRoom.id);
      toast.success('Habitación marcada como disponible');
      setModalOpen(false);
      fetchRoomsAndReservations();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const statusCounts = getStatusCounts();

  // Get today's arrivals and departures alerts
  const todayArrivals = reservations.filter(res => 
    isSameDay(parseISO(res.fecha_checkin), today)
  );
  const todayDepartures = reservations.filter(res => 
    isSameDay(parseISO(res.fecha_checkout), today)
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="habitaciones-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
              Habitaciones
            </h1>
            <p className="text-muted-foreground mt-1">
              Estado de habitaciones - {format(today, "EEEE, d 'de' MMMM yyyy", { locale: es })}
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder="Selecciona propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(() => {
              const selProp = properties.find(p => p.id === selectedProperty);
              return selProp?.codigo_unico ? (
                <span className="text-xs font-bold tracking-wider bg-primary/10 text-primary rounded-full px-3 py-1">
                  {selProp.codigo_unico}
                </span>
              ) : null;
            })()}
          </div>
        </div>

        {/* Alerts */}
        {(todayArrivals.length > 0 || todayDepartures.length > 0) && (
          <div className="grid gap-4 md:grid-cols-2">
            {todayArrivals.length > 0 && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <LogIn className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-semibold text-blue-800">
                      {todayArrivals.length} llegada(s) hoy
                    </p>
                    <p className="text-sm text-blue-600">
                      {todayArrivals.map(r => r.guest_name).join(', ')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            {todayDepartures.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-800">
                      {todayDepartures.length} salida(s) hoy
                    </p>
                    <p className="text-sm text-red-600">
                      {todayDepartures.map(r => r.guest_name).join(', ')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={filter === 'todos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('todos')}
              >
                Todos ({rooms.length})
              </Button>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <Button
                  key={key}
                  variant={filter === key ? 'default' : 'outline'}
                  size="sm"
                  className={filter === key ? config.badgeColor : ''}
                  onClick={() => setFilter(key)}
                >
                  <span className={`w-2 h-2 rounded-full mr-2 ${config.badgeColor}`}></span>
                  {config.label} ({statusCounts[key]})
                </Button>
              ))}
              
              <div className="flex-1 min-w-[200px] ml-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar habitación..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Room Doors Grid */}
        {filteredRooms.length === 0 ? (
          <Card className="p-12 text-center">
            <DoorOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No hay habitaciones</p>
            <p className="text-muted-foreground">Agrega habitaciones en el calendario</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
            {filteredRooms.map((room, index) => {
              const { status, reservation } = getRoomStatus(room);
              const config = STATUS_CONFIG[status];
              const StatusIcon = config.icon;

              return (
                <div
                  key={room.id}
                  onClick={() => handleRoomClick(room)}
                  className={`cursor-pointer group transition-all duration-300 hover:scale-[1.03]`}
                  data-testid={`room-door-${room.id}`}
                >
                  {/* Door Frame */}
                  <div className="relative bg-slate-700 rounded-xl p-[6px] shadow-lg hover:shadow-xl transition-shadow">
                    {/* Edit/Delete buttons on hover */}
                    <div className="absolute -top-2 -right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); openEditRoom(room); }} className="w-6 h-6 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white" title="Editar" data-testid={`edit-room-${room.id}`}>
                        <Wrench className="h-3 w-3 text-white" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteRoomConfirm(room); }} className="w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white" title="Eliminar" data-testid={`delete-room-${room.id}`}>
                        <Trash2 className="h-3 w-3 text-white" />
                      </button>
                    </div>
                    {/* Door Body */}
                    <div className="relative bg-slate-800 rounded-lg overflow-hidden" style={{ minHeight: '220px' }}>
                      
                      {/* Room Number Plate */}
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
                        <div className="bg-gradient-to-b from-amber-200 to-amber-300 px-4 py-1.5 rounded shadow-md border border-amber-400/50">
                          <span className="text-slate-900 font-bold text-lg tracking-wider" style={{ fontFamily: 'Manrope' }}>
                            {room.numero || (index + 1)}
                          </span>
                        </div>
                      </div>

                      {/* Upper Panel */}
                      <div className="absolute top-14 left-3 right-3 h-[60px] border border-slate-600/60 rounded-sm bg-slate-750" 
                           style={{ background: 'linear-gradient(180deg, rgba(51,65,85,0.4) 0%, rgba(30,41,59,0.2) 100%)' }}>
                      </div>

                      {/* Lower Panel */}
                      <div className="absolute top-[100px] left-3 right-3 bottom-12 border border-slate-600/60 rounded-sm"
                           style={{ background: 'linear-gradient(180deg, rgba(51,65,85,0.4) 0%, rgba(30,41,59,0.2) 100%)' }}>
                      </div>

                      {/* Door Handle */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-10">
                        {/* Lock/Card Reader */}
                        <div className="w-5 h-7 bg-slate-600 rounded-sm border border-slate-500 flex items-center justify-center shadow-inner">
                          <div className={`w-2 h-2 rounded-full ${config.ledColor} shadow-[0_0_6px_1px] ${config.doorGlow}`}></div>
                        </div>
                        {/* Handle */}
                        <div className="w-3 h-10 bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500 rounded-full shadow-md border border-amber-600/30"></div>
                      </div>

                      {/* Peephole */}
                      <div className="absolute top-[52px] left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-slate-900 border-2 border-slate-500 shadow-inner"></div>

                      {/* Room Name - on lower panel */}
                      <div className="absolute bottom-3 left-0 right-0 text-center px-2">
                        <p className="text-white/90 text-xs font-medium truncate">{room.nombre}</p>
                        {room.tipo && (
                          <p className="text-slate-400 text-[10px] truncate">{room.tipo}</p>
                        )}
                      </div>

                      {/* Door subtle wood grain texture */}
                      <div className="absolute inset-0 opacity-[0.03]" 
                           style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)' }}>
                      </div>
                    </div>

                    {/* Status Strip at bottom of frame */}
                    <div className={`mt-[6px] rounded-md ${config.doorAccent} py-1.5 px-2 flex items-center justify-center gap-1.5`}>
                      <StatusIcon className="h-3 w-3 text-white" />
                      <span className="text-white text-[11px] font-semibold">{config.label}</span>
                    </div>
                  </div>

                  {/* Guest name below door */}
                  {reservation && (
                    <p className="text-xs text-center mt-1.5 text-slate-600 font-medium truncate px-1">
                      {reservation.guest_name}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Room Detail Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedRoom && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                    <DoorOpen className="h-5 w-5" />
                    {selectedRoom.nombre}
                    {selectedReservation && (
                      <span className="text-muted-foreground font-normal">
                        - {selectedReservation.guest_name}
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_CONFIG[selectedRoom.currentStatus]?.badgeColor}>
                      {STATUS_CONFIG[selectedRoom.currentStatus]?.label}
                    </Badge>
                    {selectedRoom.tipo && (
                      <Badge variant="outline">{selectedRoom.tipo}</Badge>
                    )}
                    {selectedRoom.capacidad && (
                      <Badge variant="outline">{selectedRoom.capacidad} personas</Badge>
                    )}
                  </div>

                  {selectedRoom.currentStatus === 'mantenimiento' || selectedRoom.currentStatus === 'limpieza' ? (
                    /* Room in Maintenance/Cleaning - Show maintenance view */
                    <div className="text-center py-8">
                      <Wrench className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                      <p className="text-lg font-medium">Habitación en {selectedRoom.currentStatus === 'mantenimiento' ? 'Mantenimiento' : 'Limpieza'}</p>
                      <p className="text-muted-foreground">
                        Esta habitación requiere atención antes de estar disponible
                      </p>
                      <div className="flex gap-2 justify-center mt-6">
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                          Cerrar
                        </Button>
                        <Button 
                          className="bg-slate-800 hover:bg-slate-700"
                          onClick={handleSetAvailable}
                          data-testid="set-available-btn"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Marcar Disponible
                        </Button>
                      </div>
                    </div>
                  ) : selectedReservation ? (
                    <>
                      {/* Reservation Info */}
                      <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                        <div>
                          <Label className="text-xs text-muted-foreground">Tarifa</Label>
                          <p className="font-medium">{selectedReservation.fuente || 'Estándar'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Noches</Label>
                          <p className="font-medium">
                            {Math.ceil((new Date(selectedReservation.fecha_checkout) - new Date(selectedReservation.fecha_checkin)) / (1000 * 60 * 60 * 24))}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Valor Hospedaje</Label>
                          <p className="font-bold text-lg">${selectedReservation.precio_total?.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-blue-600 mb-1">
                            <LogIn className="h-4 w-4" />
                            <span className="text-sm font-medium">Fecha de ingreso</span>
                          </div>
                          <p className="text-sm">
                            {format(parseISO(selectedReservation.fecha_checkin), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                          </p>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-red-600 mb-1">
                            <X className="h-4 w-4" />
                            <span className="text-sm font-medium">Fecha de salida</span>
                          </div>
                          <p className="text-sm">
                            {format(parseISO(selectedReservation.fecha_checkout), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                          </p>
                        </div>
                      </div>

                      {/* Collapsible Sections */}
                      <Collapsible open={expandedSections.cliente} onOpenChange={() => toggleSection('cliente')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 border rounded-lg hover:bg-muted/50">
                          <span className="font-medium">Cliente/Contacto</span>
                          {expandedSections.cliente ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="p-3 border border-t-0 rounded-b-lg">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span>{selectedReservation.guest_name}</span>
                            </div>
                            {selectedReservation.guest_email && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Email:</span>
                                <span>{selectedReservation.guest_email}</span>
                              </div>
                            )}
                            {selectedReservation.guest_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span>{selectedReservation.guest_phone}</span>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible open={expandedSections.huespedes} onOpenChange={() => toggleSection('huespedes')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 border rounded-lg hover:bg-muted/50">
                          <span className="font-medium">Registro de huéspedes</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-orange-100">
                              TRA 0/{selectedReservation.num_huespedes || 1}
                            </Badge>
                            <Badge variant="secondary">{selectedReservation.num_huespedes || 1}</Badge>
                            {expandedSections.huespedes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="p-3 border border-t-0 rounded-b-lg">
                          <p className="text-sm text-muted-foreground">
                            {selectedReservation.num_huespedes || 1} huésped(es) registrados
                          </p>
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible open={expandedSections.consumos} onOpenChange={() => toggleSection('consumos')}>
                        <div className="flex items-center gap-2 border rounded-lg">
                          <CollapsibleTrigger className="flex items-center justify-between flex-1 p-3 hover:bg-muted/50">
                            <span className="font-medium">Consumos y servicios</span>
                            <div className="flex items-center gap-2">
                              {roomCharges.length > 0 && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200">{roomCharges.length}</Badge>
                              )}
                              {expandedSections.consumos ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </div>
                          </CollapsibleTrigger>
                          {selectedReservation && selectedReservation?.estado !== 'checked_out' && selectedReservation?.estado !== 'cancelada' && (
                            <Button size="sm" onClick={(e) => { e.stopPropagation(); openConsumoDialog(); }} className="bg-amber-600 hover:bg-amber-700 mr-2 shrink-0" data-testid="add-consumo-btn">
                              <Plus className="h-4 w-4 mr-1" />Agregar
                            </Button>
                          )}
                        </div>
                        <CollapsibleContent className="p-3 border border-t-0 rounded-b-lg space-y-3">
                          {roomCharges.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-2">Sin consumos adicionales</p>
                          ) : (
                            <div className="space-y-2">
                              {roomCharges.map(ch => (
                                <div key={ch.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2" data-testid={`charge-item-${ch.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{ch.product_name}</p>
                                    <p className="text-xs text-gray-400">
                                      x{ch.cantidad} - {new Date(ch.created_at).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                      {ch.charged_by && ` - ${ch.charged_by}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="font-semibold">${ch.total?.toLocaleString()}</span>
                                    {selectedReservation?.estado !== 'checked_out' && selectedReservation?.estado !== 'cancelada' && (
                                      <button onClick={() => handleDeleteCharge(ch.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-charge-${ch.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <div className="flex justify-between pt-2 border-t font-semibold text-sm">
                                <span>Total Consumos:</span>
                                <span className="text-amber-700" data-testid="total-consumos">${roomCharges.reduce((s, c) => s + (c.total || 0), 0).toLocaleString()}</span>
                              </div>
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Resumen de Pagos */}
                      <div className="p-4 border rounded-lg bg-muted/30" data-testid="payment-summary-section">
                        <p className="font-medium text-sm mb-3">Resumen de Pagos</p>
                        {(() => {
                          const totalConsumos = roomCharges.reduce((s, c) => s + (c.total || 0), 0);
                          const totalReserva = selectedReservation.precio_total || 0;
                          const totalGeneral = totalReserva + totalConsumos;
                          const totalPagado = reservationPaymentInfo?.total_pagado || 0;
                          const saldoPendiente = totalGeneral - totalPagado;
                          return (
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex justify-between bg-white border rounded-lg px-3 py-2">
                                  <span className="text-muted-foreground">Hospedaje</span>
                                  <span className="font-medium">${totalReserva.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between bg-white border rounded-lg px-3 py-2">
                                  <span className="text-muted-foreground">Consumos</span>
                                  <span className="font-medium text-amber-700">${totalConsumos.toLocaleString()}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div className="text-center p-3 bg-white border rounded-lg" data-testid="payment-total-reserva">
                                  <p className="text-lg font-bold">${totalGeneral.toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground mt-1">Total General</p>
                                </div>
                                <div className="text-center p-3 bg-white border rounded-lg" data-testid="payment-valor-abonado">
                                  <p className="text-lg font-bold text-green-600">${totalPagado.toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground mt-1">Valor Abonado</p>
                                </div>
                                <div className={`text-center p-3 rounded-lg ${saldoPendiente > 0 ? 'bg-yellow-500' : 'bg-green-500'}`} data-testid="payment-saldo-pendiente">
                                  <p className="text-lg font-bold text-white">${saldoPendiente.toLocaleString()}</p>
                                  <p className="text-xs text-white/80 mt-1">Saldo Pendiente</p>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Observations */}
                      <div className="space-y-2">
                        <Label className="text-red-500">Observaciones:</Label>
                        <Textarea
                          placeholder="Observaciones y detalles del hospedaje"
                          defaultValue={selectedReservation.notas || ''}
                          rows={3}
                        />
                      </div>

                      {/* TRA Checkbox */}
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="tra" className="rounded" />
                        <Label htmlFor="tra" className="text-sm">
                          Reportar huéspedes al TRA o SIRE (si aplica)
                        </Label>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 pt-4 border-t">
                        <Button variant="outline" size="sm" onClick={openGuestForm} data-testid="guest-registration-btn">
                          Tarjeta de registro
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-300 hover:bg-red-50"
                          onClick={() => setDeleteReservationOpen(true)}
                          data-testid="delete-reservation-btn"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Eliminar Reserva
                        </Button>
                        <div className="flex-1"></div>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                          Cerrar
                        </Button>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => setPaymentModalOpen(true)}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pagos
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.get(
                                `${BACKEND_URL}/api/reservations/${selectedReservation.id}/checkout-check`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setCheckoutData(res.data);
                              setTransferDialogOpen(true);
                            } catch (e) { toast.error('Error al cargar datos'); }
                          }}
                          data-testid="transfer-account-btn"
                        >
                          <ArrowRightLeft className="h-4 w-4 mr-2" />
                          Trasladar Cuenta
                        </Button>
                        <Button className="bg-yellow-600 hover:bg-yellow-700">
                          <Gift className="h-4 w-4 mr-2" />
                          Bonos
                        </Button>
                        {selectedReservation.estado === 'checked_in' ? (
                          <Button 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={handleCheckOut}
                            data-testid="checkout-btn"
                          >
                            <LogOut className="h-4 w-4 mr-2" />
                            Checkout
                          </Button>
                        ) : (
                          <Button 
                            className="bg-slate-800 hover:bg-slate-700"
                            onClick={handleCheckIn}
                            data-testid="checkin-btn"
                            disabled={selectedReservation.estado === 'checked_out'}
                          >
                            <LogIn className="h-4 w-4 mr-2" />
                            Checkin
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    /* No Reservation - Room is Available */
                    <div className="text-center py-8">
                      <Check className="h-16 w-16 mx-auto text-green-500 mb-4" />
                      <p className="text-lg font-medium">Habitación Disponible</p>
                      <p className="text-muted-foreground">
                        No hay reservas para hoy en esta habitación
                      </p>
                      <div className="flex gap-2 justify-center mt-6">
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                          Cerrar
                        </Button>
                        <Button className="bg-primary">
                          Crear Reserva
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <PaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          reservation={selectedReservation}
          onPaymentComplete={() => {
            fetchRoomsAndReservations();
            if (selectedReservation) {
              fetchPaymentInfoForReservation(selectedReservation.id);
            }
          }}
        />

        {/* Checkout Validation Dialog */}
        <AlertDialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                Cuenta con Saldo Pendiente
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>No se puede realizar checkout porque la cuenta tiene saldo pendiente.</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Total Reserva:</span>
                      <span className="font-semibold">${checkoutData?.precio_total?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Pagado:</span>
                      <span className="font-semibold text-green-600">${checkoutData?.total_pagado?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold border-t pt-1">
                      <span>Saldo Pendiente:</span>
                      <span className="text-red-600">${checkoutData?.saldo_pendiente?.toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="text-sm font-medium">Seleccione una opción:</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2">
              {checkoutData?.clientes_preferenciales?.length > 0 && (
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700 justify-start"
                  onClick={() => setCreditDialogOpen(true)}
                  data-testid="send-to-credit-option"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Enviar a Crédito (Cliente Preferencial)
                </Button>
              )}
              {checkoutData?.otras_reservas?.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full border-indigo-300 text-indigo-600 hover:bg-indigo-50 justify-start"
                  onClick={() => setTransferDialogOpen(true)}
                  data-testid="transfer-option"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Trasladar Cuenta a Otra Habitación
                </Button>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transfer Dialog */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-indigo-600" />
                Trasladar Cuenta
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Todos los pagos y saldos de esta habitación se moverán a la habitación destino.
                Esta habitación quedará con cuenta en $0.
              </p>
              <div className="space-y-2">
                <Label>Seleccionar Habitación Destino</Label>
                <Select value={transferTarget} onValueChange={setTransferTarget}>
                  <SelectTrigger data-testid="transfer-target-select">
                    <SelectValue placeholder="Seleccione habitación destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {checkoutData?.otras_reservas?.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.room_name} - {r.guest_name || 'Sin huésped'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancelar</Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleTransfer}
                  disabled={!transferTarget}
                  data-testid="confirm-transfer-btn"
                >
                  Trasladar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Credit Dialog */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                Enviar a Crédito
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm font-medium">Monto a Crédito: <span className="text-purple-700">${checkoutData?.saldo_pendiente?.toLocaleString()}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Cliente Preferencial</Label>
                <Select value={creditClient} onValueChange={setCreditClient}>
                  <SelectTrigger data-testid="credit-client-select">
                    <SelectValue placeholder="Seleccione cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {checkoutData?.clientes_preferenciales?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} ({c.tipo === 'empresa' ? c.empresa_nombre : c.documento})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Plazo de Pago</Label>
                <Select value={creditPlazo} onValueChange={setCreditPlazo}>
                  <SelectTrigger data-testid="credit-plazo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 días</SelectItem>
                    <SelectItem value="15">15 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>Cancelar</Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleSendToCredit}
                  disabled={!creditClient}
                  data-testid="confirm-credit-btn"
                >
                  Enviar a Crédito y Checkout
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Guest Registration Dialog */}
        <Dialog open={guestFormOpen} onOpenChange={setGuestFormOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Tarjeta de Registro
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tipo de Documento</Label>
                  <Select value={guestForm.guest_tipo_documento} onValueChange={(v) => setGuestForm({...guestForm, guest_tipo_documento: v})}>
                    <SelectTrigger data-testid="guest-tipo-doc"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cedula">Cédula de Ciudadanía</SelectItem>
                      <SelectItem value="pasaporte">Pasaporte</SelectItem>
                      <SelectItem value="cedula_extranjeria">Cédula de Extranjería</SelectItem>
                      <SelectItem value="tarjeta_identidad">Tarjeta de Identidad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nacionalidad</Label>
                  <Select value={guestForm.guest_nacionalidad} onValueChange={(v) => setGuestForm({...guestForm, guest_nacionalidad: v})}>
                    <SelectTrigger data-testid="guest-nacionalidad"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nacional">Nacional (Colombia)</SelectItem>
                      <SelectItem value="extranjero">Extranjero</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Número de Documento</Label>
                <Input placeholder="1234567890" value={guestForm.guest_documento} onChange={(e) => setGuestForm({...guestForm, guest_documento: e.target.value})} data-testid="guest-documento" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nombre</Label>
                  <Input placeholder="Nombre" value={guestForm.guest_name} onChange={(e) => setGuestForm({...guestForm, guest_name: e.target.value})} data-testid="guest-nombre" />
                </div>
                <div className="space-y-2">
                  <Label>Apellido</Label>
                  <Input placeholder="Apellido" value={guestForm.guest_lastname} onChange={(e) => setGuestForm({...guestForm, guest_lastname: e.target.value})} data-testid="guest-apellido" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input placeholder="+57 300..." value={guestForm.guest_phone} onChange={(e) => setGuestForm({...guestForm, guest_phone: e.target.value})} data-testid="guest-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" placeholder="correo@email.com" value={guestForm.guest_email} onChange={(e) => setGuestForm({...guestForm, guest_email: e.target.value})} data-testid="guest-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ciudad de Origen</Label>
                <Input placeholder="¿De qué ciudad viene el huésped?" value={guestForm.guest_ciudad_origen} onChange={(e) => setGuestForm({...guestForm, guest_ciudad_origen: e.target.value})} data-testid="guest-ciudad-origen" />
              </div>
              {guestForm.guest_nacionalidad === 'nacional' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>Nota:</strong> Al ser huésped nacional, se aplicará IVA del 19% en los informes.
                </div>
              )}
              {guestForm.guest_nacionalidad === 'extranjero' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <strong>Nota:</strong> Huésped extranjero — exento de IVA en los informes.
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setGuestFormOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveGuest} disabled={savingGuest} data-testid="save-guest-btn">
                {savingGuest ? 'Guardando...' : 'Guardar Registro'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Reservation Dialog */}
        <AlertDialog open={deleteReservationOpen} onOpenChange={setDeleteReservationOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar esta reserva?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará la reserva de <strong>{selectedReservation?.guest_name || 'huésped'}</strong> y todos sus pagos asociados. La habitación quedará disponible nuevamente. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingReservation}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDeleteReservation}
                disabled={deletingReservation}
                data-testid="confirm-delete-reservation"
              >
                {deletingReservation ? 'Eliminando...' : 'Eliminar Reserva'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* CONSUMO DIALOG */}
        <Dialog open={consumoDialogOpen} onOpenChange={setConsumoDialogOpen}>
          <DialogContent className="max-w-md" data-testid="consumo-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-amber-600" />Agregar Consumo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Producto *</Label>
                <Select value={consumoForm.product_id} onValueChange={v => setConsumoForm(f => ({ ...f, product_id: v }))}>
                  <SelectTrigger data-testid="consumo-product-select"><SelectValue placeholder="Seleccionar producto..." /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{p.nombre}</span>
                          <span className="text-xs text-gray-400 ml-2">${p.precio_venta?.toLocaleString()} | Stock: {p.stock_actual}</span>
                        </div>
                      </SelectItem>
                    ))}
                    {products.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No hay productos disponibles</div>}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Cantidad</Label>
                  <Input type="number" min="1" value={consumoForm.cantidad}
                    onChange={e => setConsumoForm(f => ({ ...f, cantidad: e.target.value }))} data-testid="consumo-cantidad" />
                </div>
                <div className="space-y-1.5">
                  <Label>Total</Label>
                  <p className="text-lg font-bold pt-1" data-testid="consumo-total">
                    ${((products.find(p => p.id === consumoForm.product_id)?.precio_venta || 0) * parseInt(consumoForm.cantidad || 0)).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConsumoDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddConsumo} disabled={consumoSaving || !consumoForm.product_id}
                className="bg-amber-600 hover:bg-amber-700" data-testid="consumo-save-btn">
                {consumoSaving ? 'Cargando...' : 'Cargar Consumo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Room Modal */}
        <Dialog open={editRoomOpen} onOpenChange={setEditRoomOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-room-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>Editar Habitación</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre *</Label>
                  <Input value={editRoomForm.nombre} onChange={e => setEditRoomForm({ ...editRoomForm, nombre: e.target.value })} data-testid="edit-room-nombre" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipo</Label>
                  <Input value={editRoomForm.tipo} onChange={e => setEditRoomForm({ ...editRoomForm, tipo: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Capacidad</Label>
                  <Input type="number" min="1" value={editRoomForm.capacidad} onChange={e => setEditRoomForm({ ...editRoomForm, capacidad: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Baños</Label>
                  <Input type="number" min="0" value={editRoomForm.num_banos} onChange={e => setEditRoomForm({ ...editRoomForm, num_banos: e.target.value })} />
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2 bg-slate-50/50">
                <Label className="text-xs font-semibold text-slate-600">Distribución</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Camas</Label>
                    <Input type="number" min="0" value={editRoomForm.num_camas} onChange={e => setEditRoomForm({ ...editRoomForm, num_camas: e.target.value })} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Tipo camas</Label>
                    <Input value={editRoomForm.tipo_camas} onChange={e => setEditRoomForm({ ...editRoomForm, tipo_camas: e.target.value })} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Hab. internas</Label>
                    <Input type="number" min="0" value={editRoomForm.num_habitaciones_internas} onChange={e => setEditRoomForm({ ...editRoomForm, num_habitaciones_internas: e.target.value })} className="h-8 text-sm" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Distribución</Label>
                  <Input value={editRoomForm.distribucion} onChange={e => setEditRoomForm({ ...editRoomForm, distribucion: e.target.value })} className="h-8 text-sm" placeholder="Sala, cocina, 2 hab." />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Amenidades</Label>
                <div className="flex flex-wrap gap-1.5">
                  {['WiFi','TV','Aire acondicionado','Ventilador','Minibar','Caja fuerte','Balcón','Terraza','Vista al mar','Cocina','Jacuzzi','Piscina privada','Baño privado','Agua caliente','Estacionamiento','Desayuno incluido'].map(a => {
                    const active = (editRoomForm.amenidades || []).includes(a);
                    return (
                      <button key={a} type="button" onClick={() => {
                        const list = editRoomForm.amenidades || [];
                        setEditRoomForm({ ...editRoomForm, amenidades: active ? list.filter(x => x !== a) : [...list, a] });
                      }} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Descripción</Label>
                <Input value={editRoomForm.descripcion} onChange={e => setEditRoomForm({ ...editRoomForm, descripcion: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditRoomOpen(false)}>Cancelar</Button>
              <Button onClick={saveEditRoom} className="bg-slate-800 hover:bg-slate-700" data-testid="save-edit-room">Guardar Cambios</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Room Confirmation */}
        <AlertDialog open={!!deleteRoomConfirm} onOpenChange={o => { if (!o) setDeleteRoomConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar habitación?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará <strong>{deleteRoomConfirm?.nombre}</strong> permanentemente. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteRoom} data-testid="confirm-delete-room">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
